import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Mental Map — vue React staircase + debrief.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { DIRECTION_ANGLES, DIRECTION_LABEL, staircaseConfig, } from './params';
const DIRECTION_KEY = {
    0: 'n',
    45: 'ne',
    90: 'e',
    135: 'se',
    180: 's',
    225: 'sw',
    270: 'w',
    315: 'nw',
};
function MapView({ stim, visible }) {
    const cells = [];
    for (let r = 0; r < stim.gridSize; r += 1) {
        for (let c = 0; c < stim.gridSize; c += 1)
            cells.push({ row: r, col: c });
    }
    const lmAt = new Map();
    for (const lm of stim.landmarks) {
        lmAt.set(`${lm.row}-${lm.col}`, lm);
    }
    return (_jsx("div", { className: "grid w-full max-w-[320px] gap-s1 border border-cyan-border-faint bg-cyan-surface p-s3", style: {
            gridTemplateColumns: `repeat(${stim.gridSize}, minmax(0, 1fr))`,
            aspectRatio: '1 / 1',
        }, "aria-hidden": !visible, children: cells.map((c) => {
            const lm = lmAt.get(`${c.row}-${c.col}`);
            return (_jsx("div", { className: [
                    'flex aspect-square items-center justify-center text-[16px]',
                    'bg-cyan-bg transition-opacity duration-feedback',
                    visible ? 'opacity-100' : 'opacity-0',
                ].join(' '), children: lm ? lm.token : '' }, `${c.row}-${c.col}`));
        }) }));
}
function DirectionCompass({ onPick, expected, pick, disabled, feedback }) {
    const RADIUS = 90;
    return (_jsxs("div", { className: "relative h-[220px] w-[220px]", children: [_jsx("div", { className: "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none font-display text-[14px] text-cyan-text-secondary", children: "?" }), DIRECTION_ANGLES.map((a) => {
                const isExpected = expected === a;
                const isPicked = pick === a;
                let cls = 'border border-cyan-border bg-cyan-surface text-cyan-text hover:bg-cyan-bg';
                if (feedback === 'correct' && (isExpected || isPicked))
                    cls = 'border-2 border-cyan-core bg-cyan-core text-cyan-surface';
                else if (feedback === 'incorrect' && isExpected)
                    cls = 'border-2 border-cyan-core bg-cyan-core text-cyan-surface';
                else if (feedback === 'incorrect' && isPicked)
                    cls = 'border-2 border-cyan-media bg-cyan-media text-cyan-surface';
                return (_jsx("button", { type: "button", disabled: disabled, onClick: () => onPick(a), "aria-label": DIRECTION_LABEL[a], className: [
                        'absolute left-1/2 top-1/2 flex h-[44px] w-[44px] -translate-x-1/2 -translate-y-1/2',
                        'items-center justify-center rounded-full font-mono text-[11px]',
                        'transition duration-feedback ease-soft-wind',
                        cls,
                        disabled ? 'cursor-not-allowed' : 'active:scale-95',
                    ].join(' '), style: {
                        transform: `translate(-50%, -50%) rotate(${a}deg) translateY(-${RADIUS}px) rotate(-${a}deg)`,
                    }, children: DIRECTION_LABEL[a] }, a));
            })] }));
}
export function MMStaircaseView({ adapter, level: _level, onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const handlePick = useCallback((d) => adapter.handleDirectionPick(d), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const stim = adapterState.stim;
    const fromLm = stim ? stim.landmarks.find((l) => l.id === stim.fromId) : null;
    const toLm = stim ? stim.landmarks.find((l) => l.id === stim.toId) : null;
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsxs("div", { className: "flex h-[28px] items-center justify-center text-[13px] text-cyan-text-secondary", children: [adapterState.subPhase === 'pre_exploration' && _jsx("span", { children: t('mental_map.phases.watching') }), adapterState.subPhase === 'exploration' && _jsx("span", { children: t('mental_map.phases.encoding') }), adapterState.subPhase === 'pre_test' && _jsx("span", { children: t('mental_map.phases.your_turn') }), adapterState.subPhase === 'awaiting_response' && fromLm && toLm && (_jsx("span", { children: t('mental_map.question', {
                            toName: toLm.name,
                            toToken: toLm.token,
                            fromName: fromLm.name,
                            fromToken: fromLm.token,
                        }) })), adapterState.subPhase === 'feedback' && fromLm && toLm && (_jsx("span", { className: [
                            'font-display',
                            adapterState.feedback === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: adapterState.feedback === 'correct'
                            ? `✓ ${t('mental_map.feedback.correct')}`
                            : `✗ ${t('mental_map.feedback.incorrect', {
                                name: toLm.name,
                                direction: stim
                                    ? t(`mental_map.directions.${DIRECTION_KEY[stim.expectedDirection]}`)
                                    : '?',
                            })}` }))] }), (adapterState.subPhase === 'exploration' || adapterState.subPhase === 'pre_exploration') &&
                stim && _jsx(MapView, { stim: stim, visible: adapterState.subPhase === 'exploration' }), (adapterState.subPhase === 'awaiting_response' || adapterState.subPhase === 'feedback') &&
                stim && (_jsx(DirectionCompass, { onPick: handlePick, expected: adapterState.subPhase === 'feedback' ? stim.expectedDirection : undefined, disabled: adapterState.subPhase !== 'awaiting_response', feedback: adapterState.feedback })), _jsxs("div", { className: "text-[11px] text-cyan-text-secondary", children: [t('mental_map.landmarks_label'), ' ', _jsx("span", { className: "font-mono", children: stim?.landmarks.length ?? '—' }), ' · ', t('mental_map.grid_label'), ' ', _jsxs("span", { className: "font-mono", children: [adapterState.variant.gridSize, "\u00D7", adapterState.variant.gridSize] })] })] }));
}
function recommendKey(span) {
    if (span !== null && span >= 8) {
        return 'mental_map.debrief.recommend_excellent';
    }
    if (span !== null && span >= 5) {
        return 'mental_map.debrief.recommend_good';
    }
    return 'mental_map.debrief.recommend_keep';
}
export function MMDebriefView({ level: _level, onAbort: _onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const span = staircase.thresholdEstimate;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0 ? 0 : Math.round((trialsCorrect / trialsCompleted) * 100);
    const bestSpan = useMemo(() => {
        let best = 0;
        for (const tt of staircase.trialLog) {
            if (tt.response.correct && tt.value > best)
                best = tt.value;
        }
        return Math.round(best);
    }, [staircase]);
    const fallback = !!staircase.fallback;
    const spanLabel = span === null ? '—' : span.toFixed(1).replace(/\.0$/, '');
    const replay = () => window.location.reload();
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('mental_map.debrief.title'), scoreLabel: t('mental_map.debrief.score_label'), scoreValue: spanLabel, equivalentAge: t('mental_map.accuracy_global', { accuracy }), fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
            { label: t('mental_map.debrief.metric_best_n'), value: bestSpan },
        ], recommendation: t(recommendKey(span)), scientificCaveat: t('mental_map.debrief.scientific_caveat'), ctaReplay: _jsx("button", { type: "button", onClick: replay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') }) }));
}
//# sourceMappingURL=staircaseView.js.map